import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Checkbox,
  FormControlLabel,
  Alert,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Star, Home } from '@mui/icons-material';
import { useAuth } from '@/contexts/AuthContext';

interface BecomeHostButtonProps {
  variant?: 'button' | 'cta';
  size?: 'small' | 'medium' | 'large';
}

export const BecomeHostButton: React.FC<BecomeHostButtonProps> = ({
  variant = 'button',
  size = 'medium',
}) => {
  const { canBecomeHost, upgradeToHost, error, clearError } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false);

  // Don't render if user can't become a host
  if (!canBecomeHost()) {
    return null;
  }

  const handleOpenModal = () => {
    clearError();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setTermsAccepted(false);
    clearError();
  };

  const handleUpgrade = async () => {
    if (!termsAccepted) {
      return;
    }

    try {
      setIsUpgrading(true);
      await upgradeToHost();
      handleCloseModal();
      
      // Show success message with refresh suggestion
      // Note: In a real app, you might want to use a toast notification system
      alert('Successfully upgraded to host! If you encounter any issues accessing host features, please refresh the page.');
      
    } catch (error) {
      // Error is handled by AuthContext
      console.error('Host upgrade failed:', error);
    } finally {
      setIsUpgrading(false);
    }
  };

  if (variant === 'cta') {
    return (
      <>
        <Box
          sx={{
            p: 3,
            border: '2px dashed',
            borderColor: 'primary.main',
            borderRadius: 2,
            textAlign: 'center',
            bgcolor: 'primary.light',
            color: 'primary.contrastText',
          }}
        >
          <Home sx={{ fontSize: 48, mb: 2, color: 'primary.main' }} />
          <Typography variant="h6" gutterBottom>
            Ready to become a host?
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Start earning by sharing your space with travelers
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={handleOpenModal}
            startIcon={<Star />}
          >
            Become a Host
          </Button>
        </Box>

        <HostUpgradeModal
          open={isModalOpen}
          onClose={handleCloseModal}
          onUpgrade={handleUpgrade}
          termsAccepted={termsAccepted}
          onTermsChange={setTermsAccepted}
          isUpgrading={isUpgrading}
          error={error}
        />
      </>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        size={size}
        onClick={handleOpenModal}
        startIcon={<Star />}
        sx={{
          color: 'white',
          borderColor: 'white',
          '&:hover': {
            borderColor: 'white',
            bgcolor: 'rgba(255,255,255,0.1)',
          },
        }}
      >
        Become a Host
      </Button>

      <HostUpgradeModal
        open={isModalOpen}
        onClose={handleCloseModal}
        onUpgrade={handleUpgrade}
        termsAccepted={termsAccepted}
        onTermsChange={setTermsAccepted}
        isUpgrading={isUpgrading}
        error={error}
      />
    </>
  );
};

interface HostUpgradeModalProps {
  open: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  termsAccepted: boolean;
  onTermsChange: (accepted: boolean) => void;
  isUpgrading: boolean;
  error: string | null;
}

const HostUpgradeModal: React.FC<HostUpgradeModalProps> = ({
  open,
  onClose,
  onUpgrade,
  termsAccepted,
  onTermsChange,
  isUpgrading,
  error,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Star color="primary" />
          <Typography variant="h6">Become a Host</Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" gutterBottom>
          Welcome to hosting! By becoming a host, you'll be able to:
        </Typography>

        <Box component="ul" sx={{ pl: 2, my: 2 }}>
          <Typography component="li" variant="body2" gutterBottom>
            List your properties and manage bookings
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Access calendar and availability management
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Track your earnings and expenses
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Communicate with guests through our messaging system
          </Typography>
          <Typography component="li" variant="body2" gutterBottom>
            Access host-specific tools and analytics
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You'll still have access to all guest features for your own travels.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <FormControlLabel
          control={
            <Checkbox
              checked={termsAccepted}
              onChange={(e) => onTermsChange(e.target.checked)}
              disabled={isUpgrading}
            />
          }
          label={
            <Typography variant="body2">
              I agree to the{' '}
              <Typography
                component="span"
                color="primary"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Host Terms of Service
              </Typography>{' '}
              and{' '}
              <Typography
                component="span"
                color="primary"
                sx={{ textDecoration: 'underline', cursor: 'pointer' }}
              >
                Community Guidelines
              </Typography>
            </Typography>
          }
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} disabled={isUpgrading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={onUpgrade}
          disabled={!termsAccepted || isUpgrading}
          startIcon={isUpgrading ? <CircularProgress size={16} /> : <Star />}
        >
          {isUpgrading ? 'Upgrading...' : 'Become a Host'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};